import { useMemo } from 'react';
import type { Profile } from '../lib/supabase';

// Role types
export type UserRole = 'Travel Agent' | 'Collaborator' | 'Admin';

// Permission flags
export interface RolePermissions {
  // Navigation / Tabs
  canViewClients: boolean;
  canViewGuides: boolean;
  canViewSuppliers: boolean;
  canViewTravel: boolean;

  // Itinerary actions
  canCreateItinerary: boolean;
  canUploadItinerary: boolean;
  canDeleteItinerary: boolean;

  // Agency features
  canViewAgencyItineraries: boolean;
  canManageAgency: boolean;

  // Profile display
  isCollaborator: boolean;
  displayLabel: string;
}

// Role configuration
const ROLE_CONFIG: Record<UserRole, RolePermissions> = {
  'Admin': {
    canViewClients: true,
    canViewGuides: true,
    canViewSuppliers: true,
    canViewTravel: true,
    canCreateItinerary: true,
    canUploadItinerary: true,
    canDeleteItinerary: true,
    canViewAgencyItineraries: true,
    canManageAgency: true,
    isCollaborator: false,
    displayLabel: 'Admin',
  },
  'Travel Agent': {
    canViewClients: true,
    canViewGuides: true,
    canViewSuppliers: true,
    canViewTravel: true,
    canCreateItinerary: true,
    canUploadItinerary: true,
    canDeleteItinerary: true,
    canViewAgencyItineraries: true,
    canManageAgency: false,
    isCollaborator: false,
    displayLabel: 'Travel Agent',
  },
  'Collaborator': {
    canViewClients: false,
    canViewGuides: false,
    canViewSuppliers: false,
    canViewTravel: false,
    canCreateItinerary: false,
    canUploadItinerary: false,
    canDeleteItinerary: false,
    canViewAgencyItineraries: false,
    canManageAgency: false,
    isCollaborator: true,
    displayLabel: 'Collaborator',
  },
};

// Default permissions during loading (restrictive but not marked as collaborator)
const DEFAULT_PERMISSIONS: RolePermissions = {
  ...ROLE_CONFIG['Collaborator'],
  isCollaborator: false, // Don't mark as collaborator during loading
};

/**
 * Hook to get role-based permissions for the current user
 * @param profile - The user's profile (can be null during loading)
 * @returns Permission flags based on the user's role
 */
export function useRolePermissions(profile: Profile | null): RolePermissions {
  return useMemo(() => {
    if (!profile) {
      return DEFAULT_PERMISSIONS;
    }

    const role = (profile.role as UserRole) || 'Travel Agent';
    return ROLE_CONFIG[role] || DEFAULT_PERMISSIONS;
  }, [profile?.role]);
}

/**
 * Helper to get display name for a profile
 * Collaborators show their name, agents show agency name
 */
export function getProfileDisplayName(profile: Profile | null): string {
  if (!profile) return '';

  const role = profile.role as UserRole;
  if (role === 'Collaborator' || !profile.agency_name) {
    return `${profile.first_name} ${profile.last_name}`;
  }

  return profile.agency_name;
}

/**
 * Helper to get initials for avatar
 * Collaborators use their name initials, agents use agency initials
 */
export function getProfileInitials(profile: Profile | null): string {
  if (!profile) return 'U';

  const role = profile.role as UserRole;
  if (role === 'Collaborator' || !profile.agency_name) {
    const first = profile.first_name?.[0] || '';
    const last = profile.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  }

  return profile.agency_name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
